import { createSelector } from '@app/utils/createSelector'

import type { GetRequestsQuery } from '@app/packages/api/v2/types/params'
import { intoResult, unwrapResult } from '@app/packages/Result/Result'
import { createLoadable, mapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'

import { defaultAnnouncementMeta } from '@app/store/misc/announcements'
import { announcementResponsesModelsSelector } from '@app/store/slices/announcement_responses'
import { announcementsModelsMetaSelector, announcementsModelsSelector } from '@app/store/slices/announcements'
import { locationsSelector } from '@app/store/slices/entities'
import { requestsListIdsSelector, requestsWithDebtIdsResultSelector } from '@app/store/slices/requests'
import { requestsModelsSelector } from '@app/store/slices/requests_models'
import { usersModelsSelector as usersSelector } from '@app/store/slices/users'
import type { Selector } from '@app/store/store'
import type { StoreUserParent, StoreUserSitter } from '@app/store/types/users'

export const createRequestsFetchCompleteSelector = (metaSelector: (state: unknown) => { next?: GetRequestsQuery | null }) => (state: unknown) => {
  const meta = metaSelector(state)
  return meta.next === null
}

export const requestByIdSelectorActor = (
  requestId: string,
  models: ReturnType<typeof requestsModelsSelector>,
  announcementResponses: ReturnType<typeof announcementResponsesModelsSelector>,
  announcements: ReturnType<typeof announcementsModelsSelector>,
  announcements_meta: ReturnType<typeof announcementsModelsMetaSelector>,
  users: ReturnType<typeof usersSelector>,
  locations: ReturnType<typeof locationsSelector>
) => {
  return {
    id: requestId,
    result: intoResult(() => {
      const request = models[requestId]
      if (!request) throw new Error(`Request with id ${requestId} not found`)

      const announcement =
        request.relationships.announcement && request.relationships.announcement.data ? announcements[request.relationships.announcement.data.id] : null
      if (announcement === undefined) throw new Error(`Announcement for request ${requestId} not found`)
      const announcement_meta =
        (request.relationships.announcement && request.relationships.announcement.data && announcements_meta[request.relationships.announcement.data.id]) ||
        defaultAnnouncementMeta

      const announcement_response =
        request.relationships.announcement_response && request.relationships.announcement_response.data
          ? announcementResponses[request.relationships.announcement_response.data.id]
          : null
      if (announcement_response === undefined) throw new Error(`Announcement response for request ${requestId} not found`)

      if (!request.relationships.parent || !request.relationships.parent.data)
        throw new Error(`Parent relationship for request ${requestId} is missing or malformed`)
      const parent = users[request.relationships.parent.data.id] as StoreUserParent
      if (!parent) throw new Error(`Parent for request ${requestId} not found`)

      if (!request.relationships.sitter || !request.relationships.sitter.data)
        throw new Error(`Sitter relationship for request ${requestId} is missing or malformed`)
      const sitter = users[request.relationships.sitter.data.id] as StoreUserSitter
      if (!sitter) throw new Error(`Sitter for request ${requestId} not found`)

      if (!request.relationships.location || !request.relationships.location.data)
        throw new Error(`Location relationship for request ${requestId} is missing or malformed`)
      const location = locations[request.relationships.location.data.id]
      if (!location) throw new Error(`Location for request ${requestId} not found`)

      const announcement_response_bundle = announcement_response
        ? {
            response: announcement_response,
            announcement,
            location,
            parent,
            sitter,
          }
        : null

      const announcement_bundle = announcement
        ? {
            announcement,
            meta: announcement_meta,
            location,
            parent,
            sitter,
            responses: announcement_response_bundle ? [announcement_response_bundle] : [],
          }
        : null

      return {
        request,
        announcement: announcement_bundle,
        announcement_response: announcement_response_bundle,
        location,
        parent,
        sitter,
      }
    }),
  }
}

export type RequestBundle = ReturnType<typeof requestByIdSelectorActor>

export const createRequestByIdSelector = (requestIdSelector: Selector<string>) =>
  createSelector(
    [
      requestIdSelector,
      requestsModelsSelector,
      announcementResponsesModelsSelector,
      announcementsModelsSelector,
      announcementsModelsMetaSelector,
      usersSelector,
      locationsSelector,
    ],
    requestByIdSelectorActor
  )

export const requestsListSelector = createSelector(
  [
    requestsListIdsSelector,
    requestsModelsSelector,
    announcementResponsesModelsSelector,
    announcementsModelsSelector,
    announcementsModelsMetaSelector,
    usersSelector,
    locationsSelector,
  ],
  (ids, models, responses, announcements, announcements_meta, users, locations) => {
    return ids.map(id => requestByIdSelectorActor(id, models, responses, announcements, announcements_meta, users, locations))
  }
)

export const requestsWithDebtResultSelector = createSelector(
  [
    requestsWithDebtIdsResultSelector,
    requestsModelsSelector,
    announcementResponsesModelsSelector,
    announcementsModelsSelector,
    announcementsModelsMetaSelector,
    usersSelector,
    locationsSelector,
  ],
  (idsResult, models, responses, announcements, announcements_meta, users, locations) =>
    createLoadable(c => {
      const ids = c.unwrap(idsResult)
      return ids.map(id => requestByIdSelectorActor(id, models, responses, announcements, announcements_meta, users, locations))
    })
)

export const debtTotalResultSelector = mapThunkSelector(requestsWithDebtResultSelector, items => {
  return intoResult(() => {
    return Object.entries(
      items.reduce<{ [key: string]: number }>((acc, r) => {
        const bundle = unwrapResult(r.result)
        const amount = bundle.request.attributes.amount
        if (!amount) return acc
        acc[amount.service_fee_currency ?? ''] = (acc[amount.service_fee_currency ?? ''] || 0) + parseFloat(amount.service_fee)
        return acc
      }, {})
    )
  })
})
